import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { BarElement, CategoryScale, Chart as ChartJS, ChartOptions, Legend, LinearScale, Tooltip } from 'chart.js';
import { Empty } from 'antd';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { AgentMapSeatDto } from 'types';
import { AGENT_STATUS } from 'utils';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, ChartDataLabels, Legend);

interface DashboardChartProps {
  onlineAgentsForChart: AgentMapSeatDto[];
  lastSocketChartUpdateTime: number;
  refreshData?: () => void; // Add callback prop for data refresh
}

// Status mapping for agent statuses to display names and colors
const STATUS_MAPPING = [
  { statusName: ['LOGIN', 'FAILED_LOGIN', 'LOGGEDIN'], displayStatus: 'Không bật', color: '#E8F4FC' },
  { statusName: [AGENT_STATUS.ONCALL, 'ON_CALL', 'CALL'], displayStatus: 'Đàm thoại', color: '#D1131D' },
  { statusName: [AGENT_STATUS.RINGING, 'RING'], displayStatus: 'Đang reo', color: '#AF52DE' },
  {
    statusName: [AGENT_STATUS.CREATING_TICKET, 'CREATE_TICKET', 'TICKET'],
    displayStatus: 'Tạo phiếu',
    color: '#1564E8'
  },
  { statusName: [AGENT_STATUS.AGENT_PAUSED, 'PAUSED', 'PAUSE'], displayStatus: 'Tạm nghỉ', color: '#FF9500' },
  {
    statusName: [AGENT_STATUS.AGENT_UNPAUSED, 'UNPAUSED', 'UNPAUSED_AGENT', 'READY'],
    displayStatus: 'Sẵn sàng',
    color: '#06A77D'
  }
];

const DashboardChart: React.FC<DashboardChartProps> = ({
  onlineAgentsForChart,
  lastSocketChartUpdateTime,
  refreshData
}) => {
  // Add a state to force re-render
  const [forceUpdate, setForceUpdate] = useState(0);
  const [animateOnce, setAnimateOnce] = useState(true);

  // Add ref for tracking the midnight timer
  const midnightTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Force re-render when chart data changes
  useEffect(() => {
    setForceUpdate((prev) => prev + 1);
  }, [onlineAgentsForChart, lastSocketChartUpdateTime]);

  useEffect(() => {
    // after mounting, disable further animations
    setAnimateOnce(false);

    // Set up the midnight refresh timer
    const setupMidnightTimer = () => {
      // Calculate time until next midnight
      const now = new Date();
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);

      const timeUntilMidnight = tomorrow.getTime() - now.getTime();

      // console.log(`📊 Chart: Setting up midnight refresh in ${Math.round(timeUntilMidnight/1000/60)} minutes`);

      // Clear any existing timer
      if (midnightTimerRef.current) {
        clearTimeout(midnightTimerRef.current);
      }

      // Set new timer
      midnightTimerRef.current = setTimeout(() => {
        // console.log('📊 Chart: Midnight reached - refreshing chart data');

        // Call refresh function if provided
        if (typeof refreshData === 'function') {
          refreshData();
        }

        // Force chart update
        setForceUpdate((prev) => prev + 1);

        // Set up next day's timer
        setupMidnightTimer();
      }, timeUntilMidnight);
    };

    // Initial setup
    setupMidnightTimer();

    // Clean up timer on unmount
    return () => {
      if (midnightTimerRef.current) {
        clearTimeout(midnightTimerRef.current);
        midnightTimerRef.current = null;
      }
    };
  }, [refreshData]);

  // Calculate agent status counts
  const agentStatusCounts = useMemo(() => {
    // Track unmapped statuses
    const unmappedStatuses = new Set<string>();

    const statusCounts: Record<string, number> = {};
    STATUS_MAPPING.forEach((status) => {
      statusCounts[status.displayStatus] = 0;
    });

    if (onlineAgentsForChart?.length > 0) {
      onlineAgentsForChart.forEach((seat) => {
        if (seat.extensionId && seat.statusName !== 'LOGGEDOFF' && seat.statusName !== 'UNKNOWN') {
          const statusInfo = STATUS_MAPPING.find((s) =>
            Array.isArray(s.statusName)
              ? seat.statusName && s.statusName.includes(seat.statusName)
              : s.statusName === seat.statusName
          );

          if (statusInfo) {
            statusCounts[statusInfo.displayStatus] = (statusCounts[statusInfo.displayStatus] || 0) + 1;
          } else {
            // Log unmapped status
            if (seat.statusName) {
              unmappedStatuses.add(seat.statusName);
            }
            console.warn(`DashboardChart: Unmapped status: '${seat.statusName}' for seat ${seat.agentMapSeatId}`);
          }
        }
      });
    }

    // Log unmapped statuses
    if (unmappedStatuses.size > 0) {
      console.warn(
        `DashboardChart: Found ${unmappedStatuses.size} unmapped status types:`,
        Array.from(unmappedStatuses)
      );
    }

    // Create final data structure
    return STATUS_MAPPING.map((status) => ({
      status: status.displayStatus,
      count: statusCounts[status.displayStatus] || 0,
      color: status.color
    }));
  }, [onlineAgentsForChart, lastSocketChartUpdateTime, forceUpdate]);

  // Calculate total number of unique online agents
  const totalOnlineAgentsForChart = useMemo(() => {
    if (!onlineAgentsForChart || onlineAgentsForChart.length === 0) return 0;

    const uniqueExtensionIds = new Set<string>();
    onlineAgentsForChart.forEach((seat) => {
      if (seat.extensionId && seat.statusName !== 'LOGGEDOFF' && seat.statusName !== 'UNKNOWN') {
        uniqueExtensionIds.add(seat.extensionId);
      }
    });
    return uniqueExtensionIds.size;
  }, [onlineAgentsForChart]);

  // Chart data
  const barData = {
    labels: agentStatusCounts.map((item) => item.status),
    datasets: [
      {
        label: 'Số lượng',
        data: agentStatusCounts.map((item) => item.count),
        backgroundColor: agentStatusCounts.map((item) => item.color)
      }
    ]
  };

  // Chart options
  const barOptions: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    plugins: {
      legend: { display: false },
      datalabels: {
        anchor: 'start',
        align: 'left',
        color: 'black',
        font: { weight: 'bold' },
        offset: 0
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { display: false }
      },
      y: {
        grid: { display: false },
        ticks: {
          color: '#181818',
          padding: 20
        }
      }
    },
    layout: {
      padding: {
        left: 0,
        right: 50,
        top: 10,
        bottom: 10
      }
    },
    elements: {
      bar: {
        borderRadius: 5,
        borderSkipped: 'start'
      }
    },
    animation: {
      // use configured duration on mount, then no animation
      duration: animateOnce ? 400 : 0
    }
  };

  return (
    <>
      <div className='mb-3 flex items-center justify-between'>
        <h2 className='text-lg font-semibold'>
          Trạng thái Điện thoại viên (Tổng trực tuyến: {totalOnlineAgentsForChart})
        </h2>
      </div>
      <div className='chart-container relative h-64 w-full'>
        <Bar key={`chart-${lastSocketChartUpdateTime || forceUpdate}`} data={barData} options={barOptions} />
        {(!onlineAgentsForChart || onlineAgentsForChart.length === 0) && (
          <div className='absolute inset-0 flex items-center justify-center bg-white bg-opacity-70'>
            <Empty description='Không có dữ liệu điện thoại viên trực tuyến' />
          </div>
        )}
      </div>
    </>
  );
};

export default React.memo(DashboardChart);
